import { convertJrToSotetsu, getDirectionFromTrainNo } from './TrainNumber'
import MasterData from '../data/MasterData'

/**
 * 東急線APIのレスポンスを共通フォーマットに変換する
 * TrainPositionReducer の RECEIVE_TOKYU_TRAIN_POSITION でも使用される
 */
export const extractTokyuTrains = (trains) => {
  let tokyuTrains = []

  trains.forEach((tokyuTrain) => {
    const trainKindId = (MasterData.trainKindByTokyuTrainKindId(tokyuTrain.train_kind) || {}).id

    if (!trainKindId) {
      return
    }

    const destinationStation = MasterData.stationByTokyuDestinationCode(tokyuTrain.destination_station_code)
    const destinationStationId = destinationStation ? destinationStation.station_id : null

    if (tokyuTrain.section_id) {
      const nextTokyuKYID = MasterData.nextTokyuKYIDBySectionId(tokyuTrain.section_id)
      const nextStation = MasterData.stations().find(station => station.tokyuKYId === nextTokyuKYID)
      if (nextStation) {
        tokyuTrains.push({
          station_id: null,
          next_station_id: nextStation.id,
          position: 1,
          direction: tokyuTrain.up ? 'up' : 'down',
          train_kind_id: trainKindId,
          delay: tokyuTrain.delay_time,
          train_number: tokyuTrain.train_number.substr(1, 6),
          operation_number: tokyuTrain.operation_number,
          train_length_id: tokyuTrain.num_of_cars,
          destination_station_code: tokyuTrain.destination_station_code,
          destination_station_id: destinationStationId,
          line_id: 3,
          train_line_id: tokyuTrain.train_line_id,
          source: 'tokyu',
        })
      }
    } else {
      const station = MasterData.stations().find(station => station.tokyuKYId === tokyuTrain.station_id)
      if (station && !(station.id === 29 && !tokyuTrain.up)) { // 新横浜下りは相鉄窓のデータを用いるため取得しない
        tokyuTrains.push({
          station_id: station.id,
          next_station_id: null,
          position: 1,
          direction: tokyuTrain.up ? 'up' : 'down',
          train_kind_id: trainKindId,
          delay: tokyuTrain.delay_time,
          train_number: tokyuTrain.train_number.substr(1, 6),
          operation_number: tokyuTrain.operation_number,
          train_length_id: tokyuTrain.num_of_cars,
          destination_station_code: tokyuTrain.destination_station_code,
          destination_station_id: destinationStationId,
          line_id: 3,
          train_line_id: tokyuTrain.train_line_id,
          source: 'tokyu',
        })
      }
    }
  })
  return tokyuTrains
}

/**
 * JR線APIのレスポンスを共通フォーマットに変換する
 * エラーチェック（U10002等）は呼び出し元（reducer / action creator）で行うこと
 * TrainPositionReducer の RECEIVE_JR_TRAIN_POSITION でも使用される
 */
export const extractJrTrains = (payload) => {
  let jrTrains = []
  const allStations = Object.values(MasterData.stations())

  payload.trainLocationInfo.forEach((trainLocationInfo) => {
    if (trainLocationInfo.lineCode !== '0000000000093' || trainLocationInfo.deliverStatus !== 1) {
      alert('JR線のデータを読み込めませんでした。')
      return
    }

    ;(trainLocationInfo.directionInfo || []).forEach((directionInfo) => {
      // JR線区間の列車順序のソートは本来jr_train_location_info.jsonのorderによって行われるべきだが、
      // orderの値が正しく送られて来ないので、当面の対処として列車番号でソートしている。(同一種別内では列車番号順に出発しているため)
      const trainInfos = (directionInfo.trainInfo || []).sort((a, b) => {
        const aTrainNumber = Number(convertJrToSotetsu(a.trainNo))
        const bTrainNumber = Number(convertJrToSotetsu(b.trainNo))
        if (aTrainNumber < bTrainNumber) return -1
        if (aTrainNumber > bTrainNumber) return 1
        return 0
      })

      trainInfos.forEach((trainInfo, pos) => {
        // 相鉄線の仕様に合わせて、next_station_idが入っている場合にはstation_id(前駅)はnullにする
        let stationId, nextStationId
        let position = pos
        const direction = getDirectionFromTrainNo(trainInfo.trainNo)
        const trainKindId = (MasterData.trainKindByJrTrainType(trainInfo.trainType) || {}).id
        const trainNumber = convertJrToSotetsu(trainInfo.trainNo)

        if (!trainKindId) { return } // JRの種別一覧に存在しない列車データは読み込まない
        if (!trainNumber) { return } // 列車番号が不正なものは読み込まない

        if (trainInfo.stationTo) {
          stationId = null
          nextStationId = (allStations.find(s => s.jreast_id === trainInfo.stationTo) || {}).id
        } else if (trainInfo.station) {
          stationId = (allStations.find(s => s.jreast_id === trainInfo.station) || {}).id
          nextStationId = null
        }

        // 上り羽沢〜小杉間に複数列車が在線するときは、JR、相鉄の順に並べる
        if (MasterData.isSectionBetweenHazawaToKosugi(nextStationId, direction)) {
          position = position - 100
        }

        // 下り小杉〜羽沢間に複数列車が在線するときは、相鉄、JRの順に並べる
        if (MasterData.isSectionBetweenKosugiToHazawa(nextStationId, direction)) {
          position = position + 100
        }

        jrTrains.push({
          station_id: stationId,
          next_station_id: nextStationId,
          position: position,
          direction: direction,
          train_kind_id: trainKindId,
          delay: trainInfo.delayTime,
          train_number: trainNumber,
          operation_number: trainInfo.trainIdentification,
          train_length_id: 10,
          destination_station_id: (allStations.find(s => s.jreast_id === trainInfo.organizationDetail[0].destination) || {}).id,
          line_id: 4,
          source: 'jr',
        })
      })
    })
  })

  // JR提供のデータには、同じ列車番号で複数の列車データが存在することがあるので、これを除外する
  return jrTrains.filter((item, index, array) =>
    array.findIndex(item2 => item.train_number === item2.train_number) === index
  )
}
